import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, type AppointmentWireItem } from '../lib/api-client';

/**
 * useListAppointmentsRange — grade do calendário do atendente (Brief 2B B4,
 * `GET /v1/appointments?from&to`). `assignedToUserId='me'` resolve ao caller.
 * O range (`from`/`to`) é server-side; as vistas são montadas client-side.
 */
export function useListAppointmentsRange(
  params: Parameters<typeof api.listAppointmentsRange>[0],
): ReturnType<typeof useQuery<{ data: AppointmentWireItem[] }, Error>> {
  return useQuery({
    queryKey: ['appointments', 'range', params],
    queryFn: () => api.listAppointmentsRange(params),
    staleTime: 30_000,
  });
}

/**
 * useCreateAppointment — cria um appointment (ADR-115) a partir da Conversa.
 *
 * Em sucesso invalida o prefixo `['appointments']`, refrescando o bloco
 * "Appointments relacionados" do painel direito (`use-panel-fixtures`).
 */
export function useCreateAppointment(): ReturnType<
  typeof useMutation<AppointmentWireItem, Error, Parameters<typeof api.createAppointment>[0]>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof api.createAppointment>[0]) => api.createAppointment(body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

/** Variáveis do PATCH de appointment (confirmar/cancelar/editar). */
interface UpdateAppointmentVars {
  appointmentId: string;
  expectedVersion: number;
  body: Parameters<typeof api.updateAppointment>[2];
}

/**
 * useUpdateAppointment — PATCH do appointment (`PATCH /v1/appointments/:id`)
 * usado por Confirmar (`status='confirmed'`) no bloco do painel direito.
 *
 * Em sucesso invalida o prefixo `['appointments']`.
 */
export function useUpdateAppointment(): ReturnType<
  typeof useMutation<AppointmentWireItem, Error, UpdateAppointmentVars>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, expectedVersion, body }: UpdateAppointmentVars) =>
      api.updateAppointment(appointmentId, expectedVersion, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

/** Variáveis do no-show (Brief 2B B4 + If-Match ADR-071). */
interface MarkNoShowVars {
  appointmentId: string;
  expectedVersion: number;
  body?: Parameters<typeof api.markNoShowAppointment>[2];
}

/**
 * useMarkNoShowAppointment — marca falta (`POST /v1/appointments/:id/no-show`),
 * gerando a Pendency de follow-up (D6). Em sucesso invalida `['appointments']`.
 */
export function useMarkNoShowAppointment(): ReturnType<
  typeof useMutation<AppointmentWireItem, Error, MarkNoShowVars>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, expectedVersion, body }: MarkNoShowVars) =>
      api.markNoShowAppointment(appointmentId, expectedVersion, body ?? {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}

/** Variáveis do reagendamento (ADR-115 + If-Match ADR-071). */
interface RescheduleAppointmentVars {
  appointmentId: string;
  expectedVersion: number;
  body: Parameters<typeof api.rescheduleAppointment>[2];
}

/**
 * useRescheduleAppointment — reagenda o appointment (`POST /v1/appointments/:id/reschedule`)
 * a partir do modal de Reagendar do painel direito.
 *
 * Em sucesso invalida o prefixo `['appointments']`.
 */
export function useRescheduleAppointment(): ReturnType<
  typeof useMutation<AppointmentWireItem, Error, RescheduleAppointmentVars>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId, expectedVersion, body }: RescheduleAppointmentVars) =>
      api.rescheduleAppointment(appointmentId, expectedVersion, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['appointments'] });
    },
  });
}
